import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";
import {
    ActionPlanScoreTargetWrite,
    ActionPlanWrite,
} from "src/api/client-api/ActionPlan/ActionPlan/useUpsertActionPlan";
import { ActionPlan, OneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { Campaign } from "src/api/client-api/useCampaign";
import { ScoreWithVariation } from "src/services/ScoreService";
import { Score } from "src/types";
import { ElligibleStartingPost } from "src/context/Actions/context";
import { useCustomForm } from "src/context/useCustomForm";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useEntityScores } from "src/context/Scores/useEntityScores";

export type GoalSimulationType = "absolute" | "percentage";

export type ActionPlanFormGoal = {
    rowId: number;
    "@id"?: IRI;
    score: ScoreWithVariation | null;
    variation: ScoreWithVariation | null;
    simulationVaryAbsolute: number | null;
    simulationVaryPercentage: number | null;
    simulationType: GoalSimulationType;
};

export type ActionPlanForm = {
    id: string | null;
    startingPostId: number | null;
    targetYear: Campaign | null;

    isGrowthDisabled: boolean;
    increasePerYear: string;

    reductionGoals: ActionPlanFormGoal[];
};

export type ActionPlanFormScoreOptions = {
    scores: ScoreWithVariation[];
};

type Options = {
    elligibleStartingPosts?: ElligibleStartingPost[] | undefined;
    scoresEnabled: Score[];
    actionPlan: OneActionPlan | null;
};
export const useActionPlanForm = (options: Options) => {
    const { actionPlan, scoresEnabled, elligibleStartingPosts } = options;
    const { campaigns } = useFoodpilotContext();
    const { allScoresWithVariations } = useEntityScores();
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    const { applicationParameters, isActionPlanSimplified, isLoading } = useApplicationParameters();
    const isSimplified = isActionPlanSimplified();
    const targetCampaignOption = applicationParameters?.["actionPlan-targetCampaign"];

    const targetCampaign = campaigns.find((camp) => camp["@id"] === targetCampaignOption) ?? null;

    const _initialPost =
        elligibleStartingPosts !== undefined && elligibleStartingPosts.length > 0 ? elligibleStartingPosts[0] : null;
    const initialPostId = _initialPost?.id ?? null;

    const makePostIRI = (postId: number | string) => {
        const postIRI = `/api/ssq-posts/${postId}`;
        return postIRI;
    };

    const allScoreOptions = allScoresWithVariations.filter((scoreWithVariation) => {
        return scoresEnabled.some((score) => score.id === scoreWithVariation.scoreId);
    });

    const targetsTheFormCannotShow: ActionPlanScoreTargetWrite[] = (actionPlan?.actionPlanScoreTargets ?? [])
        .filter(
            (target) =>
                !allScoreOptions.some(
                    (option) =>
                        option.scoreId === target.scoreId &&
                        (target.variationId === undefined || option.variationId === (target.variationId ?? null)),
                ),
        )
        .map((target) => ({
            "@id": target["@id"],
            scoreId: target.scoreId,
            variationId: target.variationId ?? null,
            simulationVaryPercentage: target.simulationVaryPercentage ?? null,
            simulationVaryAbsolute: target.simulationVaryAbsolute ?? null,
        }));

    const formHandler = useCustomForm<ActionPlan | null, ActionPlanForm, ActionPlanWrite>({
        inputData: actionPlan,
        serialize: (inputData) => {
            if (inputData === null) {
                const targetYear = isSimplified ? targetCampaign : null;
                return {
                    id: null,
                    startingPostId: initialPostId,
                    targetYear: targetYear,
                    increasePerYear: "",
                    reductionGoals: [],
                    isGrowthDisabled: true,
                };
            }

            const reductionGoals = inputData.actionPlanScoreTargets.flatMap((item, idx) => {
                const foundScore = allScoreOptions.find((score) => {
                    const isScoreId = score.scoreId === item.scoreId;

                    const itemVariation = item.variationId ?? null;
                    const isVariationId = score.variationId === itemVariation;

                    if (item.variationId === undefined) {
                        return isScoreId;
                    }
                    return isScoreId && isVariationId;
                });
                if (foundScore === undefined) {
                    return [];
                }
                const simulationType: GoalSimulationType =
                    item.simulationVaryAbsolute === undefined ? "percentage" : "absolute";

                const newGoal: ActionPlanFormGoal = {
                    rowId: idx,
                    "@id": item["@id"],

                    score: foundScore,
                    variation: foundScore,
                    simulationVaryAbsolute: item.simulationVaryAbsolute ?? null,
                    simulationVaryPercentage: item.simulationVaryPercentage ?? null,
                    simulationType,
                };
                return [newGoal];
            });

            const growth = inputData.simulationVaryPercentage;
            const increasePerYear = growth?.toString() ?? "";
            const isGrowthDisabled = growth === undefined || growth === null;

            const res: ActionPlanForm = {
                id: inputData.id,
                startingPostId: inputData.startSsqPost.id,
                targetYear: inputData.endCampaign ?? null,

                isGrowthDisabled,
                increasePerYear,
                reductionGoals,
            };
            return res;
        },
        onSubmit: (formData) => {
            try {
                const goalBlocks = formData.reductionGoals.map((goal) => {
                    const goalValue =
                        goal.simulationType === "percentage" ?
                            {
                                simulationVaryAbsolute: null,
                                simulationVaryPercentage: goal.simulationVaryPercentage,
                            }
                        :   {
                                simulationVaryAbsolute: goal.simulationVaryAbsolute,
                                simulationVaryPercentage: null,
                            };

                    const scoreId = goal.score?.scoreId;
                    const variationId = goal.variation?.variationId;
                    if (scoreId === undefined || variationId === undefined) {
                        throw new Error(
                            t(
                                "action_plan.trajectory.form.goal.incomplete_score",
                                "One of your goal has an imcomplete Score",
                            ),
                        );
                    }
                    const goalBlock: ActionPlanScoreTargetWrite = {
                        "@id": goal["@id"],
                        scoreId: scoreId,
                        variationId: variationId ?? null,
                        ...goalValue,
                    };
                    return goalBlock;
                });

                if (formData.startingPostId === null) {
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "error",
                        message: t(
                            "action_plan.trajectory.form.starting_date_missing",
                            "Initial Starting date is missing",
                        ),
                    });
                    return null;
                }
                const postIRI = makePostIRI(formData.startingPostId);

                const growth = formData.isGrowthDisabled ? null : Number(formData.increasePerYear);

                const dataWrite: ActionPlanWrite = {
                    name: t("action_plan.trajectory.form.name", "Name"),
                    description: null,
                    simulationVaryPercentage: growth,
                    startSsqPost: postIRI,
                    endCampaign: formData.targetYear?.["@id"] ?? "",
                    actionPlanScoreTargets: [...goalBlocks, ...targetsTheFormCannotShow],
                };

                if (formData.id !== undefined && formData.id !== null) {
                    dataWrite.id = formData.id;
                }

                return dataWrite;
            } catch (err) {
                const errMessage = (err as { message?: string }).message;
                if (errMessage) {
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "error",
                        message: errMessage,
                    });
                    return null;
                }
                snackbar.forceClose();
                snackbar.setSnackbarProps({
                    type: "error",
                    message: t("unknown_error"),
                });
                return null;
            }
        },
    });

    const remainingScoreOptions = getRemainingScoreOptions(allScoreOptions, formHandler.formState.reductionGoals);

    if (isLoading === true) return { ...formHandler, remainingScoreOptions: { scores: [] }, allScoreOptions: [] };

    return { ...formHandler, remainingScoreOptions: remainingScoreOptions, allScoreOptions };
};

const getRemainingScoreOptions = (allScores: ScoreWithVariation[], goals: ActionPlanFormGoal[]) => {
    const existingScoreIds = goals.reduce<number[]>((acc, value) => {
        const scoreId = value.score?.scoreId;
        if (scoreId === undefined) return acc;

        acc.push(scoreId);
        return acc;
    }, []);

    const filteredVariations = allScores.filter((score) => {
        const doesScoreExist = existingScoreIds.includes(score.scoreId);
        return doesScoreExist === false;
    });
    const scoreVariations = Object.groupBy(filteredVariations, (i) => i.scoreId);

    const optionsAcc: ActionPlanFormScoreOptions = { scores: [] };
    const remainingScoreOptions = Object.entries(scoreVariations).reduce((acc, opt) => {
        const [k, v] = opt;
        if (v === undefined) return acc;

        const existingScore = allScores.find((score) => {
            return score.scoreId.toString() === k && score.variationId === null;
        });
        if (existingScore === undefined) return acc;

        acc["scores"].push(existingScore);
        return acc;
    }, optionsAcc);

    return remainingScoreOptions;
};
